<template>
  <q-dialog persistent ref="dialog" v-model="showDialog" @hide="onDialogHide" class="qr-wallet-dialog">
    <q-card class="qr-wallet-card">
      <q-card-section class="row items-center q-pa-md">
        <div class="text-h6">Wallet Payment</div>
        <q-space />
        <q-btn icon="close" flat round dense v-close-popup @click="onCancel" />
      </q-card-section>

      <q-card-section class="q-pa-md text-center">
        <div v-if="!paymentData.paymentId" class="loading-section">
          <q-spinner size="50px" color="primary" />
          <div class="q-mt-md">Generating QR Code...</div>
        </div>

        <div v-else class="qr-section">
          <div class="text-h6 q-mb-md">Scan QR Code to Pay</div>

          <div class="qr-container">
            <img :src="paymentData.qrImage" alt="QR Code" class="qr-image" />
          </div>

          <div class="payment-details q-mt-md">
            <div class="text-subtitle1 text-weight-medium">
              Amount: {{ priceFormat(amount) }}
            </div>
            <div class="text-caption text-grey-6">
              Order ID: {{ orderId }}
            </div>
            <div class="text-caption text-grey-6">
              Payment ID: {{ paymentData.paymentId }}
            </div>
          </div>

          <div class="status-section q-mt-lg">
            <div class="row items-center justify-center q-mb-md">
              <q-spinner v-if="isPolling" size="24px" color="primary" class="q-mr-sm" />
              <div class="text-body2" :class="{ 'text-grey-6': isPolling }">
                {{ statusText }}
              </div>
            </div>

            <div class="countdown-section" v-if="isPolling">
              <div class="text-caption text-grey-6">
                Time remaining: {{ formatCountdown(countdown) }}
              </div>
              <q-linear-progress :value="countdown / maxCountdown" color="primary" size="4px" class="q-mt-xs" />
            </div>
          </div>

          <div class="actions-section q-mt-lg">
            <q-btn flat label="Cancel Payment" @click="onCancel" class="q-mr-md" />
            <q-btn color="primary" label="Check Payment Status" @click="checkPaymentStatus" :loading="isChecking" />
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import { QDialog, QCard, QCardSection, QSpace, QBtn, QSpinner, QLinearProgress } from 'quasar';

export default {
  name: 'QRWalletPaymentDialog',
  components: {
    QDialog,
    QCard,
    QCardSection,
    QSpace,
    QBtn,
    QSpinner,
    QLinearProgress
  },
  props: {
    orderId: {
      type: String,
      required: true
    },
    amount: {
      type: Number,
      required: true
    },
    currency: {
      type: String,
      default: 'THB'
    },
    saleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showDialog: false,
      paymentData: {
        paymentId: null,
        qrImage: null,
        qrRaw: null
      },
      isPolling: false,
      isChecking: false,
      countdown: 600, // 10 minutes
      maxCountdown: 600,
      pollingInterval: null,
      countdownInterval: null,
      statusText: 'Waiting for payment...'
    };
  },
  watch: {
    showDialog(newVal) {
      if (newVal) {
        this.initializePayment();
      } else {
        this.cleanup();
      }
    }
  },
  methods: {
    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      this.$refs.dialog.hide();
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit("hide");
    },
    async initializePayment() {
      try {
        const session = await this.$services.authentication.getApikey();

        console.log('Initializing QR wallet payment:', {
          amount: this.amount,
          currency: this.currency,
          orderId: this.orderId,
          hasSession: session.key,

        });

        const response = await this.$services.pay.generateQRWalletPayment({
          amount: this.amount,
          currency: this.currency,
          orderId: this.orderId,
          ticket: this.orderId,
          saleId: this.saleId,
        }, session.key);

        if (response.data) {
          this.paymentData = response.data;
          console.log('QR payment initialized successfully:', this.paymentData);
          this.startPolling();
        }
      } catch (error) {
        console.error('Failed to generate QR payment:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });

        let errorMessage = 'Failed to generate QR code';
        let errorCaption = error.message;

        if (error.response?.status === 401) {
          errorMessage = 'Authentication failed';
          errorCaption = 'Please try logging in again';
        }

        this.$q.notify({
          type: 'negative',
          message: errorMessage,
          caption: errorCaption
        });
        this.onCancel();
      }
    },

    startPolling() {
      this.isPolling = true;
      this.countdown = this.maxCountdown;

      // Start countdown
      this.countdownInterval = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          this.onTimeout();
        }
      }, 1000);

      // Start polling payment status
      this.pollingInterval = setInterval(() => {
        this.checkPaymentStatus();
      }, 5000);
    },

    async checkPaymentStatus() {
      if (!this.paymentData.paymentId) return;

      try {
        this.isChecking = true;
        const session = await this.$services.authentication.getApikey();
        console.log('Checking payment status for:', this.paymentData.paymentId);

        const response = await this.$services.pay.inquireQRWalletPayment(this.paymentData.paymentId, session.key);

        if (response.data) {
          const { paymentStatus } = response.data;

          console.log('Payment status:', paymentStatus);

          if (paymentStatus === 'SUCCESS') {
            this.onPaymentSuccess(response.data);
          } else if (paymentStatus === 'FAILED') {
            this.onPaymentFailed(response.data);
          } else {
            this.statusText = 'Waiting for payment...';
          }
        }
      } catch (error) {
        console.error('Failed to check payment status:', error);
        console.error('Payment status error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });

        // Don't show error notifications for status checks unless it's a critical error
        if (error.response?.status === 401) {
          this.onPaymentFailed({ error: 'Authentication failed' });
        }
      } finally {
        this.isChecking = false;
      }
    },

    onPaymentSuccess() {
      this.cleanup();
      window.location.href = `/thankyou?orderId=${this.orderId}&status=success&amount=${this.amount}`
    },

    onPaymentFailed(paymentData) {
      this.cleanup();
      this.$emit('payment-failed', paymentData);

      this.$q.notify({
        type: 'negative',
        message: 'Payment failed',
        caption: 'Please try again'
      });
    },

    onTimeout() {
      this.cleanup();
      this.$emit('payment-timeout');

      this.$q.notify({
        type: 'warning',
        message: 'Payment timeout',
        caption: 'The payment session has expired'
      });
    },

    onCancel() {
      this.cleanup();
      this.$emit('payment-cancelled');
    },

    cleanup() {
      this.isPolling = false;
      this.isChecking = false;

      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }

      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },

    formatCountdown(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    },

    priceFormat(amount) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    }
  },

  beforeDestroy() {
    this.cleanup();
  }
};
</script>

<style scoped>

.qr-wallet-dialog {
  z-index: 6000;
}

.qr-wallet-card {
  max-width: 500px;
  margin: auto;
}

.qr-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;
}

.qr-image {
  max-width: 250px;
  max-height: 250px;
  width: auto;
  height: auto;
}

.payment-details {
  text-align: center;
}

.status-section {
  text-align: center;
}

.countdown-section {
  max-width: 300px;
  margin: 0 auto;
}

.actions-section {
  text-align: center;
}

.loading-section {
  padding: 40px 20px;
  text-align: center;
}

@media (max-width: 600px) {
  .qr-image {
    max-width: 200px;
    max-height: 200px;
  }
}

</style>